import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import PendingUpdate from 'src/assets/images/PendingUpdate.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CollapsibleMiddleBox from 'src/components/CollapsibleMiddleBox/CollapsibleMiddleBox'
import RecurrenceAmountAndFrequencyLabel from 'src/components/RecurrenceAmountAndFrequencyLabel/RecurrenceAmountAndFrequencyLabel'
import RecurrenceEndingLabel from 'src/components/RecurrenceEndingLabel/RecurrenceEndingLabel'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { RecurrenceTerminator } from 'src/models/recurrence.models'
import {
  VirtualCardPendingRevisionModel,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { updateVirtualCardUrl } from 'src/navigation/urls'
import { css } from 'styled-components'

interface PendingModificationBoxProps {
  className?: string
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions?: VirtualCardPermissionsModel
  timezone: string
  pendingRevision: VirtualCardPendingRevisionModel
}

const InfoStack: React.FC<{
  header: React.ReactNode
  body: React.ReactElement | string | undefined
}> = (props): React.ReactElement => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;

        &:nth-child(n + 2) {
          margin-left: 48px;
        }
      `}
    >
      <span
        css={css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {props.header}
      </span>
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
          margin-top: 4px;
        `}
      >
        {props.body}
      </span>
    </div>
  )
}

const PendingModificationBox: React.FC<PendingModificationBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const topContent = (
    <div
      css={css`
        display: flex;
        align-items: center;

        padding: ${styleConstants.heights.cardPadding}
          ${styleConstants.widths.cardPaddingXSMUp};
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          padding: ${styleConstants.heights.cardPadding}
            ${styleConstants.widths.cardPaddingXXSMOnly};
        `)}
      `}
    >
      <img
        src={PendingUpdate}
        alt={''}
        css={css`
          margin-right: 24px;
          height: 60px;
        `}
      />
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.highlight,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('Request Awaiting Approval')}
      </span>
    </div>
  )

  const middleContent = (
    <div
      css={css`
        display: flex;
        flex-direction: column;

        padding: 0px ${styleConstants.widths.cardPaddingXSMUp};
        ${getBreakpointDown(breakpointSizes.XXSM)(css`
          padding: 0px ${styleConstants.widths.cardPaddingXXSMOnly};
        `)}
      `}
    >
      <div
        css={css`
          display: flex;
          margin-top: ${styleConstants.heights.cardPadding};
          margin-bottom: ${styleConstants.heights.cardPadding};
        `}
      >
        {props.pendingRevision.balance !== undefined &&
          !props.virtualCard.isIssuerCycle && (
            <InfoStack
              header={translate('Available Credit Requested')}
              body={
                <Crncy
                  currency={props.virtualCard.currency}
                  amount={props.pendingRevision.balance}
                />
              }
            />
          )}
        {props.pendingRevision.balance !== undefined &&
          props.virtualCard.isIssuerCycle && (
            <InfoStack
              header={translate('Monthly Credit Limit Requested')}
              body={
                <Crncy
                  currency={props.virtualCard.currency}
                  amount={props.pendingRevision.balance}
                  maximumFractionDigits={0}
                />
              }
            />
          )}
        {/* TODO: revert back to activeUntil */}
        {props.pendingRevision.validToTimezonedString(props.timezone) !==
          undefined && (
          <InfoStack
            header={translate('Active Until Date Requested')}
            body={props.pendingRevision.validToTimezonedString(props.timezone)}
          />
        )}
        {(props.pendingRevision.recurs === true ||
          props.pendingRevision.recurs === false) && (
          <InfoStack
            header={translate('Card Type Requested')}
            body={
              props.pendingRevision.recurs === true
                ? translate('Recurring')
                : translate('Standard')
            }
          />
        )}
        {props.pendingRevision.recurrence !== undefined && (
          <InfoStack
            header={translate('Frequency Requested')}
            body={translate(
              props.pendingRevision.recurrence.terminator ===
                RecurrenceTerminator.Count
                ? '${recurrenceAmountAndFrequencyLabel}. ${recurrenceEndingLabel}.'
                : '${recurrenceAmountAndFrequencyLabel}, ${recurrenceEndingLabel}.',
              {
                recurrenceAmountAndFrequencyLabel: (
                  <RecurrenceAmountAndFrequencyLabel
                    period={props.pendingRevision.recurrence.period}
                    balance={props.pendingRevision.recurrence.balance}
                    interval={props.pendingRevision.recurrence.interval}
                    byWeekDay={props.pendingRevision.recurrence.byWeekDay}
                    byMonthDay={props.pendingRevision.recurrence.byMonthDay}
                    byYearDay={props.pendingRevision.recurrence.byYearDay}
                    timezone={props.virtualCard.timezone}
                    currency={props.virtualCard.currency}
                  />
                ),
                recurrenceEndingLabel: (
                  <RecurrenceEndingLabel
                    terminator={props.pendingRevision.recurrence.terminator}
                    count={props.pendingRevision.recurrence.count}
                    untilMoment={props.pendingRevision.recurrence.untilTimezonedMoment(
                      props.timezone,
                    )}
                  />
                ),
              },
            )}
          />
        )}
      </div>
      {props.virtualCardPermissions?.canUpdateVirtualCard && (
        <div
          css={css`
            margin-bottom: ${styleConstants.heights.cardPadding};
          `}
        >
          <Button
            linkProps={{
              className: 'action-btn-lnk',
              to: updateVirtualCardUrl(props.virtualCard.id),
              ariaLabelSource: 'Approve or Deny Request',
            }}
            css={css`
              width: 215px;
            `}
            design={ButtonDesigns.designCta}
            type={ButtonTypes.button}
            text={translate('Approve or Deny Request')}
          />
        </div>
      )}
    </div>
  )

  const closedBottomLabel = translate('Request Details')
  const openBottomLabel = translate('Hide Request Details')

  return (
    <CollapsibleMiddleBox
      className={props.className}
      topContent={topContent}
      middleContent={middleContent}
      bottomContent={{
        closedBottomLabel: closedBottomLabel,
        openBottomLabel: openBottomLabel,
      }}
    />
  )
}

export default PendingModificationBox
